import React from "react";

import {
  formatMoney,
  formatPercent,
} from "../../../utils/formatters";

export default function FinancialPositionSummary({
  summary = {},
  settings,
}) {
  const currency =
    settings?.currency || "USD";

  const grossExposure = Number(
    summary?.total_p95_exposure ?? 0
  );

  const transferred = Number(
    summary?.total_transferred_p95 ?? 0
  );

  const capitalAtRisk = Number(
    summary?.total_capital_at_risk ??
      summary?.portfolio_gap ??
      0
  );

  const protectionRatio = normalizeRatio(
    summary?.portfolio_protection_ratio
  );

  const retainedRatio = Math.max(
    0,
    1 - protectionRatio
  );

  const scenariosCount = Number(
    summary?.scenarios_count ?? 0
  );

  const adequateCount = Number(
    summary?.adequate_count ?? 0
  );

  const marginalCount = Number(
    summary?.marginal_count ?? 0
  );

  const underinsuredCount = Number(
    summary?.underinsured_count ?? 0
  );

  const uncoveredCount = Number(
    summary?.uncovered_count ?? 0
  );

  return (
    <section className="rounded-2xl border border-slate-200 bg-white p-6">
      <div className="flex flex-col gap-4 lg:flex-row lg:items-start lg:justify-between">
        <div>
          <p className="text-xs font-semibold uppercase tracking-wide text-slate-500">
            Current Portfolio Position
          </p>

          <h2 className="mt-2 text-xl font-semibold text-slate-900">
            {formatMoney(
              capitalAtRisk,
              currency
            )}{" "}
            remains exposed
          </h2>

          <p className="mt-2 max-w-3xl text-sm text-slate-500">
            Current risk-transfer arrangements protect{" "}
            {formatPercent(
              protectionRatio,
              1
            )}{" "}
            of quantified portfolio P95 exposure across{" "}
            {scenariosCount} active scenarios.
          </p>
        </div>

        <div className="rounded-xl bg-slate-50 px-4 py-3 text-right">
          <p className="text-xs font-medium text-slate-500">
            Portfolio Protection
          </p>

          <p className="mt-1 text-3xl font-semibold text-slate-900">
            {formatPercent(
              protectionRatio,
              1
            )}
          </p>
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-end justify-between gap-4">
          <div>
            <p className="text-xs font-medium text-slate-500">
              Gross P95 Exposure
            </p>

            <p className="mt-1 text-2xl font-semibold text-slate-900">
              {formatMoney(
                grossExposure,
                currency
              )}
            </p>
          </div>

          <div className="text-right">
            <p className="text-xs font-medium text-slate-500">
              Capital at Risk
            </p>

            <p className="mt-1 text-2xl font-semibold text-red-700">
              {formatMoney(
                capitalAtRisk,
                currency
              )}
            </p>
          </div>
        </div>

        <div className="mt-4 flex h-5 w-full overflow-hidden rounded-full bg-slate-200">
          {protectionRatio > 0 && (
            <div
              className="h-full bg-emerald-500 transition-all"
              style={{
                width: `${Math.min(
                  protectionRatio * 100,
                  100
                )}%`,
              }}
              title={`Transferred: ${formatPercent(
                protectionRatio,
                1
              )}`}
            />
          )}

          {retainedRatio > 0 && (
            <div
              className="h-full bg-red-400 transition-all"
              style={{
                width: `${Math.min(
                  retainedRatio * 100,
                  100
                )}%`,
              }}
              title={`Retained: ${formatPercent(
                retainedRatio,
                1
              )}`}
            />
          )}
        </div>

        <div className="mt-3 flex flex-col gap-2 text-sm sm:flex-row sm:items-center sm:justify-between">
          <div className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-sm bg-emerald-500" />

            <span className="text-slate-600">
              Transferred{" "}
              <strong className="text-slate-900">
                {formatMoney(
                  transferred,
                  currency
                )}
              </strong>
            </span>
          </div>

          <div className="flex items-center gap-2">
            <span className="h-3 w-3 rounded-sm bg-red-400" />

            <span className="text-slate-600">
              Retained{" "}
              <strong className="text-slate-900">
                {formatMoney(
                  capitalAtRisk,
                  currency
                )}
              </strong>
            </span>
          </div>
        </div>
      </div>

      <div className="mt-6 grid grid-cols-2 gap-3 md:grid-cols-5">
        <PositionCount
          label="Scenarios"
          value={scenariosCount}
          tone="neutral"
        />

        <PositionCount
          label="Adequate"
          value={adequateCount}
          tone="adequate"
        />

        <PositionCount
          label="Marginal"
          value={marginalCount}
          tone="marginal"
        />

        <PositionCount
          label="Underinsured"
          value={underinsuredCount}
          tone="underinsured"
        />

        <PositionCount
          label="Uncovered"
          value={uncoveredCount}
          tone="uncovered"
        />
      </div>
    </section>
  );
}

function PositionCount({
  label,
  value,
  tone,
}) {
  const styles = {
    neutral:
      "border-slate-200 bg-slate-50 text-slate-900",
    adequate:
      "border-emerald-200 bg-emerald-50 text-emerald-800",
    marginal:
      "border-amber-200 bg-amber-50 text-amber-800",
    underinsured:
      "border-orange-200 bg-orange-50 text-orange-800",
    uncovered:
      "border-red-200 bg-red-50 text-red-800",
  };

  return (
    <div
      className={`rounded-xl border p-3 ${
        styles[tone] || styles.neutral
      }`}
    >
      <p className="text-xs font-medium">
        {label}
      </p>

      <p className="mt-1 text-xl font-semibold">
        {value}
      </p>
    </div>
  );
}

function normalizeRatio(value) {
  const number = Number(value ?? 0);

  if (!Number.isFinite(number)) {
    return 0;
  }

  return Math.min(
    Math.max(number, 0),
    1
  );
}